import { addDays, addMinutes, addWeeks, endOfMonth, getDay, setHours, setMinutes } from 'date-fns'
import { supabase } from './supabase'
import { createAppNotification } from './notifications'
import type { Appointment, CandidateSlot } from './types'

const ACTIVE_STATUSES = new Set(['planned', 'awaitingBusiness', 'awaitingClient', 'awaitingClientChoice', 'awaitingBusinessAfterClientChoice', 'confirmed', 'inProgress'])

interface CreateCleaningInput {
  organizationId: string
  clientId: string
  serviceName: string
  start: Date
  durationMinutes: number
  priceCents: number
  notes: string
  employeeIds: string[]
  recurrenceWeeks: number | null
  existingAppointments: Appointment[]
  bufferMinutes: number
}

export interface UpdateCleaningInput {
  clientId: string
  serviceName: string
  start: Date
  durationMinutes: number
  priceCents?: number
  notes: string
  employeeIds: string[]
}

export function conflictsWithSchedule(
  start: Date,
  durationMinutes: number,
  appointments: Appointment[],
  bufferMinutes = 30,
  excludingId?: string,
) {
  const end = addMinutes(start, durationMinutes).getTime()
  const buffer = bufferMinutes * 60_000
  return appointments.some((appointment) => {
    if (appointment.id === excludingId || !ACTIVE_STATUSES.has(appointment.status)) return false
    const occupiedStart = new Date(appointment.start_at).getTime()
    const occupiedEnd = addMinutes(occupiedStart, appointment.duration_minutes).getTime()
    return start.getTime() < occupiedEnd + buffer && end + buffer > occupiedStart
  })
}

async function actorId() {
  const { data } = await supabase.auth.getUser()
  return data.user?.id ?? null
}

export async function ensureConversation(organizationId: string, clientId: string) {
  const { data: existing, error: lookupError } = await supabase
    .from('conversations')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('client_id', clientId)
    .maybeSingle()
  if (lookupError) throw lookupError
  if (existing) return existing
  const { data, error } = await supabase
    .from('conversations')
    .insert({ id: crypto.randomUUID(), organization_id: organizationId, client_id: clientId })
    .select('*')
    .single()
  if (error) throw error
  return data
}

async function systemMessage(
  organizationId: string,
  clientId: string,
  body: string,
  appointmentId?: string,
  invoiceId?: string,
) {
  const conversation = await ensureConversation(organizationId, clientId)
  const { error } = await supabase.from('messages').insert({
    id: crypto.randomUUID(),
    organization_id: organizationId,
    conversation_id: conversation.id,
    sender: 'system',
    kind: invoiceId ? 'invoice' : appointmentId ? 'schedule' : 'system',
    body,
    sent_at: new Date().toISOString(),
    is_read: false,
    appointment_id: appointmentId ?? null,
    invoice_id: invoiceId ?? null,
    created_by: await actorId(),
  })
  if (error) throw error
}

async function notify(
  organizationId: string,
  clientId: string,
  audience: 'business' | 'client',
  title: string,
  body: string,
  appointmentId?: string,
) {
  await createAppNotification({
    organizationId,
    clientId,
    audience,
    title,
    body,
    appointmentId,
  })
}

function readableSlot(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(date)
}

export async function createCleaning(input: CreateCleaningInput) {
  if (conflictsWithSchedule(input.start, input.durationMinutes, input.existingAppointments, input.bufferMinutes)) {
    throw new Error('That time conflicts with another cleaning or the travel buffer.')
  }
  const recurrenceWeeks = input.recurrenceWeeks && [1, 2, 4].includes(input.recurrenceWeeks)
    ? input.recurrenceWeeks
    : null
  const seriesId = recurrenceWeeks ? crypto.randomUUID() : null
  const occurrences: Date[] = [input.start]
  if (recurrenceWeeks) {
    const horizon = endOfMonth(input.start)
    for (let week = recurrenceWeeks; ; week += recurrenceWeeks) {
      const occurrence = addWeeks(input.start, week)
      if (occurrence > horizon) break
      occurrences.push(occurrence)
    }
  }
  const ids = occurrences.map(() => crypto.randomUUID())
  const rows = occurrences.map((start, index) => ({
    id: ids[index],
    organization_id: input.organizationId,
    client_id: input.clientId,
    service_name: input.serviceName.trim(),
    start_at: start.toISOString(),
    duration_minutes: input.durationMinutes,
    status: index === 0 ? 'awaitingBusiness' : 'planned',
    price_cents: input.priceCents,
    candidate_slots: [],
    business_confirmed_at: null,
    client_confirmed_at: null,
    slot_hold_expires_at: null,
    revision: 0,
    notes: input.notes.trim(),
    assigned_employee_ids: input.employeeIds,
    recurrence_series_id: seriesId,
    recurrence_interval_weeks: recurrenceWeeks,
    recurrence_anchor_start: seriesId ? start.toISOString() : null,
  }))
  const { error } = await supabase.from('appointments').insert(rows)
  if (error) throw error
  const appointmentId = ids[0]
  if (!appointmentId) throw new Error('Unable to create this cleaning.')
  await systemMessage(
    input.organizationId,
    input.clientId,
    `A cleaning was proposed for ${readableSlot(input.start)}. Business approval is required before the client is notified.`,
    appointmentId,
  )
  await notify(input.organizationId, input.clientId, 'business', 'New cleaning needs approval', readableSlot(input.start), appointmentId)
  return appointmentId
}

export async function updateCleaning(appointment: Appointment, input: UpdateCleaningInput) {
  if (appointment.status === 'confirmed' && appointment.client_id !== input.clientId) {
    throw new Error('A confirmed cleaning cannot be moved to another client. Cancel it and create a new cleaning instead.')
  }

  const scheduleChanged = appointment.client_id !== input.clientId
    || appointment.start_at !== input.start.toISOString()
    || appointment.duration_minutes !== input.durationMinutes
  const confirmedReschedule = appointment.status === 'confirmed' && scheduleChanged
  const update = {
    client_id: input.clientId,
    service_name: input.serviceName.trim(),
    start_at: input.start.toISOString(),
    duration_minutes: input.durationMinutes,
    ...(input.priceCents !== undefined ? { price_cents: input.priceCents } : {}),
    notes: input.notes.trim(),
    assigned_employee_ids: input.employeeIds,
    ...(scheduleChanged ? {
      status: confirmedReschedule ? 'awaitingClient' as const : 'awaitingBusiness' as const,
      business_confirmed_at: confirmedReschedule ? new Date().toISOString() : null,
      client_confirmed_at: null,
      slot_hold_expires_at: null,
      candidate_slots: [],
      revision: appointment.revision + 1,
    } : {}),
  }

  const { data, error } = await supabase
    .from('appointments')
    .update(update)
    .eq('id', appointment.id)
    .eq('revision', appointment.revision)
    .select('id')
    .maybeSingle()
  if (error) throw error
  if (!data) throw new Error('This cleaning changed on another device. Refresh and try again.')

  if (confirmedReschedule) {
    await systemMessage(
      appointment.organization_id,
      appointment.client_id,
      `The business requested a new time: ${readableSlot(input.start)}. The client must confirm the change.`,
      appointment.id,
    )
    await notify(
      appointment.organization_id,
      appointment.client_id,
      'client',
      'Cleaning time changed',
      `Please confirm ${readableSlot(input.start)}.`,
      appointment.id,
    )
  } else if (scheduleChanged) {
    await systemMessage(
      appointment.organization_id,
      appointment.client_id,
      `The cleaning time changed to ${readableSlot(input.start)}. Both confirmations were reset.`,
      appointment.id,
    )
    await notify(
      appointment.organization_id,
      appointment.client_id,
      'business',
      'Updated cleaning needs approval',
      readableSlot(input.start),
      appointment.id,
    )
  }

  return { scheduleChanged, confirmedReschedule }
}

export async function activateDuePlannedAppointments(organizationId: string) {
  const cutoff = addDays(new Date(), 7).toISOString()
  const { data: due, error: lookupError } = await supabase
    .from('appointments')
    .select('id,organization_id,client_id,start_at,revision')
    .eq('organization_id', organizationId)
    .eq('status', 'planned')
    .lte('start_at', cutoff)
    .order('start_at')
  if (lookupError) throw lookupError

  let activatedCount = 0
  for (const appointment of (due ?? []) as Appointment[]) {
    const { data: activated, error: updateError } = await supabase
      .from('appointments')
      .update({ status: 'awaitingBusiness', revision: appointment.revision + 1 })
      .eq('id', appointment.id)
      .eq('status', 'planned')
      .eq('revision', appointment.revision)
      .select('id')
      .maybeSingle()
    if (updateError) throw updateError
    if (!activated) continue
    activatedCount += 1
    await Promise.allSettled([
      systemMessage(
        appointment.organization_id,
        appointment.client_id,
        'An upcoming recurring cleaning is ready for business approval.',
        appointment.id,
      ),
      notify(
        appointment.organization_id,
        appointment.client_id,
        'business',
        'Recurring cleaning needs approval',
        readableSlot(new Date(appointment.start_at)),
        appointment.id,
      ),
    ])
  }
  return activatedCount
}

export async function businessApprove(appointment: Appointment) {
  if (!['awaitingBusiness', 'awaitingBusinessAfterClientChoice'].includes(appointment.status)) {
    throw new Error('That approval is no longer available.')
  }
  const now = new Date()
  const finalApproval = appointment.status === 'awaitingBusinessAfterClientChoice'
  if (finalApproval && (!appointment.slot_hold_expires_at || new Date(appointment.slot_hold_expires_at) <= now)) {
    throw new Error('The temporary hold expired. Ask the client to choose a new time.')
  }
  const status = finalApproval ? 'confirmed' : 'awaitingClient'
  const { error } = await supabase.from('appointments').update({
    status,
    business_confirmed_at: now.toISOString(),
    client_confirmed_at: finalApproval ? appointment.client_confirmed_at : null,
    slot_hold_expires_at: null,
    revision: appointment.revision + 1,
  }).eq('id', appointment.id)
  if (error) throw error
  await systemMessage(
    appointment.organization_id,
    appointment.client_id,
    finalApproval
      ? 'The business confirmed the selected time. The service is now scheduled.'
      : 'The business approved the proposed time. Client confirmation is needed.',
    appointment.id,
  )
  if (finalApproval) {
    await notify(
      appointment.organization_id,
      appointment.client_id,
      'client',
      'Schedule confirmed',
      readableSlot(new Date(appointment.start_at)),
      appointment.id,
    )
  }
}

export async function clientApprove(appointment: Appointment) {
  if (appointment.status !== 'awaitingClient' || !appointment.business_confirmed_at) {
    throw new Error('The business must approve this time first.')
  }
  const { error } = await supabase.from('appointments').update({
    status: 'confirmed',
    client_confirmed_at: new Date().toISOString(),
    revision: appointment.revision + 1,
  }).eq('id', appointment.id)
  if (error) throw error
  await systemMessage(appointment.organization_id, appointment.client_id, 'The client confirmed the time. The service is now scheduled.', appointment.id)
  await notify(appointment.organization_id, appointment.client_id, 'business', 'Client confirmed', readableSlot(new Date(appointment.start_at)), appointment.id)
}

export function suggestAlternativeSlots(
  appointment: Appointment,
  appointments: Appointment[],
  bufferMinutes = 30,
  earliest = new Date(),
): CandidateSlot[] {
  const results: CandidateSlot[] = []
  const duration = appointment.duration_minutes
  const searchStart = addMinutes(earliest, 30)
  for (let offset = 0; offset < 28 && results.length < 3; offset += 1) {
    const day = addDays(searchStart, offset)
    const weekday = getDay(day)
    if (weekday === 0) continue
    const endHour = weekday === 6 ? 14 : 18
    for (let minutes = 8 * 60; minutes + duration <= endHour * 60 && results.length < 3; minutes += 30) {
      const candidate = setMinutes(setHours(day, 0), minutes)
      if (candidate <= searchStart) continue
      if (!conflictsWithSchedule(candidate, duration, appointments, bufferMinutes, appointment.id)) {
        results.push({ id: crypto.randomUUID(), start: candidate.toISOString(), durationMinutes: duration })
      }
    }
  }
  return results
}

export async function clientRequestsAlternatives(
  appointment: Appointment,
  appointments: Appointment[],
  bufferMinutes: number,
) {
  if (!['awaitingClient', 'confirmed'].includes(appointment.status)) {
    throw new Error('A new time cannot be requested for this cleaning right now.')
  }
  const alternatives = suggestAlternativeSlots(appointment, appointments, bufferMinutes, new Date())
  if (!alternatives.length) throw new Error('No open times were found. Ask the company in chat.')
  const { error } = await supabase.from('appointments').update({
    status: 'awaitingClientChoice',
    business_confirmed_at: null,
    client_confirmed_at: null,
    slot_hold_expires_at: null,
    candidate_slots: alternatives,
    revision: appointment.revision + 1,
  }).eq('id', appointment.id)
  if (error) throw error
  await systemMessage(appointment.organization_id, appointment.client_id, 'The client requested another time. TidyPing suggested available openings.', appointment.id)
  await notify(appointment.organization_id, appointment.client_id, 'business', 'Client requested another time', `${alternatives.length} openings suggested`, appointment.id)
}

export async function clientSelectsSlot(appointment: Appointment, slot: CandidateSlot) {
  if (appointment.status !== 'awaitingClientChoice' || !appointment.candidate_slots.some((item) => item.id === slot.id)) {
    throw new Error('That time is no longer available.')
  }
  const now = new Date()
  const { error } = await supabase.from('appointments').update({
    start_at: slot.start,
    duration_minutes: slot.durationMinutes,
    status: 'awaitingBusinessAfterClientChoice',
    business_confirmed_at: null,
    client_confirmed_at: now.toISOString(),
    slot_hold_expires_at: addMinutes(now, 15).toISOString(),
    revision: appointment.revision + 1,
  }).eq('id', appointment.id)
  if (error) throw error
  await systemMessage(appointment.organization_id, appointment.client_id, `The client selected ${readableSlot(new Date(slot.start))}. Business approval is needed.`, appointment.id)
  await notify(appointment.organization_id, appointment.client_id, 'business', 'New time selected', readableSlot(new Date(slot.start)), appointment.id)
}

export async function businessDeclinesChoice(
  appointment: Appointment,
  appointments: Appointment[],
  bufferMinutes: number,
) {
  if (appointment.status !== 'awaitingBusinessAfterClientChoice') throw new Error('That action is no longer available.')
  const alternatives = suggestAlternativeSlots(appointment, appointments, bufferMinutes, new Date(appointment.start_at))
  const { error } = await supabase.from('appointments').update({
    status: 'awaitingClientChoice',
    business_confirmed_at: null,
    client_confirmed_at: null,
    slot_hold_expires_at: null,
    candidate_slots: alternatives,
    revision: appointment.revision + 1,
  }).eq('id', appointment.id)
  if (error) throw error
  await systemMessage(appointment.organization_id, appointment.client_id, 'The selected time did not work for the business. Fresh openings were suggested.', appointment.id)
  await notify(appointment.organization_id, appointment.client_id, 'client', 'Please choose another time', `${alternatives.length} new openings`, appointment.id)
}

export async function cancelCleaning(
  appointment: Appointment,
  actor: 'business' | 'client',
  reason = '',
) {
  if (['completed', 'cancelled'].includes(appointment.status)) {
    throw new Error('This cleaning can no longer be cancelled.')
  }
  if (actor === 'client' && appointment.status !== 'confirmed') {
    throw new Error('Only a confirmed cleaning can be cancelled by the client.')
  }

  const cleanedReason = reason.trim()
  const { data, error } = await supabase
    .from('appointments')
    .update({
      status: 'cancelled',
      business_confirmed_at: null,
      client_confirmed_at: null,
      slot_hold_expires_at: null,
      candidate_slots: [],
      revision: appointment.revision + 1,
    })
    .eq('id', appointment.id)
    .eq('status', appointment.status)
    .eq('revision', appointment.revision)
    .select('id')
    .maybeSingle()
  if (error) throw error
  if (!data) throw new Error('This cleaning changed on another device. Refresh and try again.')

  const reasonSuffix = cleanedReason ? ` Reason: ${cleanedReason}` : ''
  await systemMessage(
    appointment.organization_id,
    appointment.client_id,
    `The ${actor} cancelled this cleaning.${reasonSuffix}`,
    appointment.id,
  )
  await notify(
    appointment.organization_id,
    appointment.client_id,
    actor === 'business' ? 'client' : 'business',
    actor === 'business' ? 'Cleaning cancelled' : 'Client cancelled cleaning',
    cleanedReason
      ? `${readableSlot(new Date(appointment.start_at))} · ${cleanedReason}`
      : readableSlot(new Date(appointment.start_at)),
    appointment.id,
  )
}

export async function setAppointmentStatus(appointment: Appointment, status: 'inProgress') {
  const { error } = await supabase.from('appointments').update({
    status,
    revision: appointment.revision + 1,
  }).eq('id', appointment.id)
  if (error) throw error
  await systemMessage(appointment.organization_id, appointment.client_id, 'The cleaning has started.', appointment.id)
}

export async function completeCleaning(appointment: Appointment, markPaid: boolean) {
  const { data, error } = await supabase.rpc('complete_cleaning', {
    p_appointment_id: appointment.id,
    p_mark_paid: markPaid,
  })
  if (error) throw error
  const result = (data as Array<{ notification_id: string | null }> | null)?.[0]
  if (result?.notification_id) {
    await supabase.functions.invoke('dispatch-notifications', {
      body: { notification_ids: [result.notification_id] },
    }).catch(() => undefined)
  }
}

export async function setCleaningPaymentStatus(appointment: Appointment, paid: boolean) {
  const { data, error } = await supabase.rpc('set_cleaning_payment_status', {
    p_appointment_id: appointment.id,
    p_paid: paid,
  })
  if (error) throw error
  const result = (data as Array<{ notification_id: string | null }> | null)?.[0]
  if (result?.notification_id) {
    await supabase.functions.invoke('dispatch-notifications', {
      body: { notification_ids: [result.notification_id] },
    }).catch(() => undefined)
  }
}

export async function renewRecurringSeries(seriesId: string) {
  const { data, error } = await supabase.rpc('renew_recurring_series', {
    p_series_id: seriesId,
  })
  if (error) throw error
  const result = (data as Array<{ series_id: string; created_count: number }> | null)?.[0]
  if (!result) throw new Error('This recurring plan is no longer available.')
  return result
}
